import { GuidedSellingStepT, GuidedSellingResult, GuidedSellingTag, NotFinalGuidedSellingStep } from "@swo/storefront";
import { LoadingState, LoadingStateOps } from "../../../domain/data-layer/loading-state";
import { useCallback, useEffect, useState } from "react";
import { getAllTags } from "../helpers/get-all-tags";
import { FinalGuidedSellingStep } from "@swo/storefront";
import { LoadingStateWithIdle, LoadingStateWithIdleOps } from "../../../domain/data-layer/loading-state-with-idle";
import { removeLastElem } from "../../../utils/js/missing-js-parts";
import { useResourceRelatedInfo } from "../../../hooks/use-resource-related-info";
import { array, either, nonEmptyArray } from "fp-ts";
import { GuidedSellingResultDto } from "@swo/storefront";
import { NonEmptyArray } from "fp-ts/es6/NonEmptyArray";
import { pipe } from "fp-ts/lib/function";
import { getMaxTreeDepth } from "./get-max-tree-depth";
import { notNullPredicate } from "../../../utils/fp/missing-ts-parts";
import { ImageWithFocalPointType } from "@swo/shared";
import { Eq } from "fp-ts/Eq";

export function calculateGSStepOfChoices(
  choicesIds: Array<string>,
  initialStep: NotFinalGuidedSellingStep,
): GuidedSellingStepT {
  return choicesIds.reduce((acc: GuidedSellingStepT, choiceId) => {
    if (acc.type === "not-final") {
      const c = [...acc.choices].find(choice => choice.id === choiceId);

      if (c) {
        return c.nextStep;
      } else {
        throw Error("Invalid step selection.");
      }
    }

    return acc;
  }, initialStep);
}

const createEmptyState = (initialStep: NotFinalGuidedSellingStep): GuidedSellingInternalState => {
  return {
    tag: "choices",
    data: {
      choicesIds: [],
      selected: undefined,
      currentStep: initialStep,
    },
  };
};

export type GuidedSellingInternalState =
  | {
      tag: "choices";
      data: { choicesIds: Array<string>; selected: string | undefined; currentStep: NotFinalGuidedSellingStep };
    }
  | GuidedSellingInternalState_Results;

export type GuidedSellingInternalState_Results = {
  tag: "results";
  data: {
    choicesIds: Array<string>;
    currentStep: FinalGuidedSellingStep;
    resultId: string;
    results: LoadingStateWithIdle<{ answers: Array<GuidedSellingResultDto>; selectedResultIdx: number }>;
  };
};

export type GuidedSellingExternalState = GuidedSellingExternalState_Choices | GuidedSellingExternalState_Results;

type Progress = { percent: number; madeSelectionsCount: number };

export type GuidedSellingExternalState_Choices = {
  tag: "choices";
  data: { selected: string | undefined; currentStep: NotFinalGuidedSellingStep; currentStepIdx: number };
  progress: Progress;
  nextBackgrounds: Array<ImageWithFocalPointType>;
};

export type GuidedSellingExternalState_Results = {
  tag: "results";
  data: {
    currentStep: FinalGuidedSellingStep;
    results: LoadingState<{
      selected: GuidedSellingResultDto | undefined;
      selectedIndex: number;
      resultsCount: number;
    }>;
  };
  progress: Progress;
};

const getProgress = (s: GuidedSellingInternalState): Progress => {
  const alreadyMadeStepsCount = s.data.choicesIds.length;

  if (s.tag === "results") {
    return {
      percent: 100,
      madeSelectionsCount: alreadyMadeStepsCount,
    };
  } else {
    const maxTreeDepthFromCurrentStep = getMaxTreeDepth(s.data.currentStep);
    const alreadyMadeStepsCountBoostedForUserEngagement = alreadyMadeStepsCount + 1;

    return {
      percent:
        (alreadyMadeStepsCountBoostedForUserEngagement /
          (alreadyMadeStepsCountBoostedForUserEngagement + maxTreeDepthFromCurrentStep)) *
        100,
      madeSelectionsCount: alreadyMadeStepsCount + (s.data.selected ? 1 : 0),
    };
  }
};

export function useGuidedSellingState(
  initialStep: NotFinalGuidedSellingStep,
  fetchGSResult: (
    selectedTags: NonEmptyArray<GuidedSellingTag>,
  ) => Promise<GuidedSellingResult<GuidedSellingResultDto>>,
) {
  const [state, setState] = useState<GuidedSellingInternalState>(createEmptyState(initialStep));
  const progress = getProgress(state);

  const onIndexRequested = (index: number) => {
    setState(prevState => {
      if (
        prevState.tag === "results" &&
        prevState.data.results.__tag === "loaded" &&
        index >= 0 &&
        index < prevState.data.results.data.answers.length
      ) {
        return {
          ...prevState,
          data: {
            ...prevState.data,
            results: LoadingStateOps.map(prevState.data.results, r => {
              return {
                ...r,
                selectedResultIdx: index,
              };
            }),
          },
        };
      } else {
        return prevState;
      }
    });
  };

  const onClickPrevious = useCallback(
    () =>
      setState(previousState => {
        const nextChoicesIds = removeLastElem(previousState.data.choicesIds);
        const lastElem = previousState.data.choicesIds.slice(-1)[0];
        const nextStep = calculateGSStepOfChoices(nextChoicesIds, initialStep);

        if (nextStep.type === "not-final") {
          return {
            tag: "choices",
            data: {
              ...previousState.data,
              choicesIds: nextChoicesIds,
              selected: lastElem,
              currentStep: nextStep,
            },
          };
        } else {
          return previousState;
        }
      }),
    [],
  );
  const onClickRestart = useCallback(() => setState(createEmptyState(initialStep)), []);

  const onClickNext = (currentChoice: string) => {
    setState(s => {
      if (s.tag === "choices") {
        const nextChoices = [...s.data.choicesIds, currentChoice];
        const nextStep = calculateGSStepOfChoices(nextChoices, initialStep);

        if (nextStep.type === "not-final") {
          return {
            tag: "choices",
            data: {
              ...s.data,
              choicesIds: nextChoices,
              selected: undefined,
              currentStep: nextStep,
            },
          };
        } else {
          return {
            tag: "results",
            data: {
              choicesIds: nextChoices,
              resultId: Date.now().toString(),
              results: LoadingStateWithIdleOps.idleS,
              currentStep: nextStep,
            },
          };
        }
      } else {
        return s;
      }
    });
  };

  const onSelectChoice = useCallback((choiceId: string) => {
    return setState(previousState => {
      if (previousState.tag === "choices") {
        return {
          ...previousState,
          data: {
            ...previousState.data,
            selected: choiceId,
          },
        };
      } else {
        return previousState;
      }
    });
  }, []);

  const asyncResults = useResourceRelatedInfo(
    state.tag === "results" ? LoadingStateOps.asLoaded(state.data) : LoadingStateWithIdleOps.idleS,
    v => v.resultId,
    data => {
      const allTags = getAllTags(data.choicesIds, initialStep);

      return pipe(
        allTags,
        nonEmptyArray.fromArray,
        either.fromOption(() => "tags-selection-empty"),
      );
    },
    data => {
      return fetchGSResult(data);
    },
  );

  useEffect(() => {
    if (asyncResults.__tag === "loaded") {
      setState(prevState => {
        if (prevState.tag === "results") {
          const results = LoadingStateOps.asLoaded({
            selectedResultIdx: 0,
            answers: typeof asyncResults.data.result === "undefined" ? [] : asyncResults.data.result.results,
          });

          return {
            ...prevState,
            data: {
              ...prevState.data,
              results,
            },
          };
        } else {
          return prevState;
        }
      });
    }
  }, [asyncResults]);

  const externalState = mapToExternalState(state, progress);

  return {
    state: externalState,
    actions: {
      onIndexRequested,
      onClickPrevious: state.data.choicesIds.length > 0 ? onClickPrevious : undefined,
      onClickNext,
      onClickRestart,
      onSelectChoice,
    },
  };
}

const imageWithFocalPointTypeEq: Eq<ImageWithFocalPointType> = {
  equals: (a, b) => {
    return a.image.url === b.image.url;
  },
};

const arrOfFocalPointUniq = array.uniq(imageWithFocalPointTypeEq);

function mapToExternalState(internalState: GuidedSellingInternalState, progress: Progress): GuidedSellingExternalState {
  if (internalState.tag === "choices") {
    const nextBackgroundFromNextStep = internalState.data.selected
      ? [...internalState.data.currentStep.choices].map(a => a.nextStep.background).filter(notNullPredicate)
      : [];

    const nextBackgroundFromChoices = internalState.data.currentStep.choices
      .map(c => c.background)
      .filter(notNullPredicate);

    const backgrounds = [...nextBackgroundFromChoices, ...nextBackgroundFromNextStep];
    const nextBackgroundsUnique = pipe(backgrounds, arrOfFocalPointUniq);

    return {
      tag: "choices",
      data: {
        selected: internalState.data.selected,
        currentStep: internalState.data.currentStep,
        currentStepIdx: internalState.data.choicesIds.length,
      },
      progress,
      nextBackgrounds: nextBackgroundsUnique,
    };
  } else if (internalState.tag === "results") {
    const resultsLs = LoadingStateWithIdleOps.toLoadingState(internalState.data.results);

    const results = LoadingStateOps.flatMap(resultsLs, r => {
      if (r.answers.length > 0) {
        const maybeSelected: GuidedSellingResultDto | undefined = r.answers[r.selectedResultIdx];

        return LoadingStateOps.asLoaded({
          selected: maybeSelected,
          selectedIndex: r.selectedResultIdx,
          resultsCount: r.answers.length,
        });
      } else {
        return LoadingStateOps.asFailed("no-results-found");
      }
    });

    return {
      tag: "results",
      data: {
        currentStep: internalState.data.currentStep,
        results,
      },
      progress,
    };
  } else {
    throw Error("Invalid state exception.");
  }
}
